import { Injectable } from '@angular/core';
import { DVCVariableSet, initializeDevCycle, type DevCycleClient } from '@devcycle/js-client-sdk';
import { environment } from 'src/environments/environment';
import { type VariableValue, type VariableTypeAlias } from '@devcycle/types';
import { Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  private client: DevCycleClient;
  private initialized = false;
  private flagEvaluatedSubject = new Subject<{ key: string; value: unknown }>();
  public readonly flagEvaluatedEvent$ = this.flagEvaluatedSubject.asObservable();

  constructor() {
    this.initializeClientAndSetFlags()
  }

  private async initializeClientAndSetFlags(): Promise<void> {
    this.client = initializeDevCycle(environment.devcycleKey, {
      isAnonymous: true,
    });

    this.client.onClientInitialized().then(async () => {
      this.initialized = true;
      console.log('[FeatureFlag] DevCycle initialized (anonymous)');

      // Forward all feature flag events to external observers
      this.client.subscribe('variableEvaluated:*', (key: string, variable) => {
        this.flagEvaluatedSubject.next({ key, value: variable.value });
      });
    });
  }

  private async ensureReady(): Promise<void> {
    if (!this.initialized) {
      await this.client.onClientInitialized();
      this.initialized = true;
    }
  }

  /**
   * Retrieves a feature flag variable.
   * @param key The DevCycle variable key
   * @param fallback The default value if the flag is not returned
   */
  async getVariable<T extends VariableValue>(key: string, fallback: T): Promise<VariableTypeAlias<T>> {
    await this.ensureReady();
    const variable = this.client.variable(key, fallback);
    return variable.value
  }

  /**
   * Returns a boolean to denote if the given feature is enabled or not
   * If the key itself is not present in DevCycle, this will return false
   * @param key The DevCycle variable key
   */
  async isFeatureEnabled(key: string): Promise<boolean> {
    const variable = await this.getVariable(key, false)
    return !!variable
  }
}